import { useMemo, useState } from 'react';

import { ApiClient, formatRelative, useAsync, useMutation } from '@clientma/core';
import {
  Alert,
  Badge,
  Button,
  Drawer,
  Select,
  Skeleton,
  Stat,
  Textarea,
  useToast,
} from '@clientma/ui';

import { providerApi, type SupportTicketRow } from '../api';

/**
 * What customers tell us, and what we said back.
 *
 * The queue and the ratings on one screen rather than two, because they are the same question
 * asked at two moments: what is waiting on us now, and how did the last few go. A satisfaction
 * score in a separate dashboard is a number nobody looks at while there is a queue.
 */
export default function Feedback({ api }: { api: ApiClient }) {
  const provider = useMemo(() => providerApi(api), [api]);

  const [filter, setFilter] = useState<'awaiting' | 'open' | 'rated' | 'all'>('awaiting');
  const [openId, setOpenId] = useState<string | null>(null);

  const { data, isLoading, error, reload } = useAsync(
    (signal) =>
      provider.tickets(
        {
          awaitingUsOnly: filter === 'awaiting' ? true : undefined,
          status: filter === 'open' ? 'open' : filter === 'rated' ? 'Closed' : undefined,
          pageSize: 50,
        },
        signal,
      ),
    [provider, filter],
  );

  const rows = data?.items ?? [];

  // Ratings come from closed tickets, so they are only meaningful on a list that contains any.
  const rated = rows.filter((row) => row.satisfactionRating != null);

  const average =
    rated.length > 0
      ? rated.reduce((sum, row) => sum + (row.satisfactionRating ?? 0), 0) / rated.length
      : null;

  return (
    <div className="cma-stack">
      <div className="provider-toolbar">
        <Select
          value={filter}
          aria-label="Which tickets"
          onChange={(event) => setFilter(event.target.value as typeof filter)}
        >
          <option value="awaiting">Waiting on us</option>
          <option value="open">Everything open</option>
          <option value="rated">Closed, with ratings</option>
          <option value="all">All</option>
        </Select>
      </div>

      <div className="cma-stat-row">
        <Stat label="Shown" value={String(rows.length)} />
        <Stat
          label="Waiting on us"
          value={String(rows.filter((row) => row.awaitingProviderReply).length)}
        />
        <Stat
          label="Average rating"
          value={average === null ? '—' : average.toFixed(2)}
          meta={`${rated.length} rated`}
        />
      </div>

      {error && (
        <Alert tone="danger" title="The tickets could not be loaded">
          {error.message}
        </Alert>
      )}

      {isLoading ? (
        <Skeleton height={280} />
      ) : rows.length === 0 ? (
        <p className="provider-muted">
          {filter === 'awaiting'
            ? 'Nothing is waiting on a reply from us.'
            : 'No tickets match that.'}
        </p>
      ) : (
        <div className="cma-stack cma-stack--tight">
          {rows.map((row) => (
            <button
              key={row.id}
              type="button"
              className="provider-ticket"
              onClick={() => setOpenId(row.id)}
            >
              <div className="provider-ticket__head">
                <strong>{row.subject}</strong>
                {row.awaitingProviderReply && <Badge tone="warning">Waiting on us</Badge>}
                <Badge tone={row.status === 'Closed' ? 'neutral' : 'info'}>{row.status}</Badge>
                {row.satisfactionRating != null && (
                  <Badge tone={row.satisfactionRating >= 4 ? 'success' : 'danger'}>
                    {'★'.repeat(row.satisfactionRating)}
                    {'☆'.repeat(5 - row.satisfactionRating)}
                  </Badge>
                )}
              </div>

              <span className="provider-muted">
                {row.ticketNumber} · {row.tenantName ?? 'Unknown customer'} ·{' '}
                {row.raisedByName ?? 'somebody'} · {formatRelative(row.lastActivityAtUtc)}
              </span>
            </button>
          ))}
        </div>
      )}

      {openId && (
        <TicketDrawer
          api={api}
          ticketId={openId}
          onClose={() => setOpenId(null)}
          onChanged={reload}
        />
      )}
    </div>
  );
}

function TicketDrawer({
  api,
  ticketId,
  onClose,
  onChanged,
}: {
  api: ApiClient;
  ticketId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const provider = useMemo(() => providerApi(api), [api]);
  const toast = useToast();

  const { data, isLoading, reload } = useAsync(
    (signal) => provider.ticket(ticketId, signal),
    [provider, ticketId],
  );

  const [body, setBody] = useState('');
  const [isInternal, setIsInternal] = useState(false);

  const reply = useMutation(() => provider.replyToTicket(ticketId, body, isInternal), {
    onSuccess: () => {
      toast.success(isInternal ? 'Internal note added' : 'Reply sent');
      setBody('');
      reload();
      onChanged();
    },
  });

  const update = useMutation(
    (patch: { status?: string; assignToMe?: boolean }) => provider.updateTicket(ticketId, patch),
    {
      onSuccess: () => {
        reload();
        onChanged();
      },
    },
  );

  return (
    <Drawer open title={data?.summary.subject ?? 'Ticket'} onClose={onClose}>
      {isLoading || !data ? (
        <Skeleton height={320} />
      ) : (
        <div className="cma-stack">
          <div className="cma-row" style={{ gap: 6, flexWrap: 'wrap' }}>
            <Badge tone="neutral">{data.summary.ticketNumber}</Badge>
            <Badge tone={data.summary.status === 'Closed' ? 'neutral' : 'info'}>
              {data.summary.status}
            </Badge>
            <Badge tone="neutral">{data.summary.priority}</Badge>
            {data.summary.assignedToName ? (
              <Badge tone="accent">{data.summary.assignedToName}</Badge>
            ) : (
              <Badge tone="warning">Unassigned</Badge>
            )}
          </div>

          <p className="provider-muted">
            {data.summary.tenantName} · raised by {data.summary.raisedByName ?? 'somebody'}
            {data.raisedByEmail ? ` (${data.raisedByEmail})` : ''}
          </p>

          <div className="provider-entry">
            <p className="provider-entry__body">{data.description}</p>
          </div>

          {data.messages.map((message) => (
            <div
              key={message.id}
              className={`provider-entry${message.isInternal ? ' provider-entry--internal' : ''}`}
            >
              <div className="provider-entry__head">
                <strong>{message.authorName}</strong>
                {/*
                  Marked, always. An internal note that looked like a reply is how somebody
                  pastes "they are three months overdue" into a message to the customer.
                */}
                {message.isInternal && <Badge tone="warning">Internal — they cannot see this</Badge>}
              </div>

              <p className="provider-entry__body">{message.body}</p>
              <p className="provider-muted">{formatRelative(message.sentAtUtc)}</p>
            </div>
          ))}

          {data.summary.satisfactionRating != null && (
            <Alert
              tone={data.summary.satisfactionRating >= 4 ? 'success' : 'warning'}
              title={`They rated this ${data.summary.satisfactionRating} out of 5`}
            >
              {data.resolution ?? 'No resolution was recorded.'}
            </Alert>
          )}

          <Textarea
            rows={4}
            value={body}
            placeholder={isInternal ? 'A note for us only…' : 'Your reply to the customer…'}
            aria-label="Reply"
            onChange={(event) => setBody(event.target.value)}
          />

          <label className="cma-check">
            <input
              type="checkbox"
              checked={isInternal}
              onChange={(event) => setIsInternal(event.target.checked)}
            />
            <span>Internal note — the customer will not see this</span>
          </label>

          {reply.error && <Alert tone="danger">{reply.error.message}</Alert>}

          <div className="cma-row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <Button
              disabled={reply.isRunning || body.trim().length === 0}
              onClick={() => void reply.run(undefined as never)}
            >
              {reply.isRunning ? 'Sending…' : isInternal ? 'Add note' : 'Reply'}
            </Button>

            {!data.summary.assignedToName && (
              <Button variant="ghost" onClick={() => void update.run({ assignToMe: true })}>
                Take it
              </Button>
            )}

            {data.summary.status !== 'Closed' && (
              <Button variant="ghost" onClick={() => void update.run({ status: 'Resolved' })}>
                Mark resolved
              </Button>
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
}
